import * as React from "react";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import HistoryIcon from "@mui/icons-material/History";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import { Badge, Divider, Chip } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { showUserPreference, createUserPreference } from "@/services";
import { useTheme } from "@mui/system";
const MAX_RECENT_PAGES = 3;
const MAX_FAVORITES = 10;
const PREFERENCE_KEY = "recentAndFavoritePages";

interface PageInfo {
  path: string;
  title: string;
  timestamp: string;
  isFavorite?: boolean;
}

const getPageTitle = (path: string): string => {
  const translations: Record<string, string> = {
    invoices: "Notas Fiscais",
    invoice: "Nota Fiscal",
    measurements: "Medições",
    measurement: "Medição",
    financial: "Financeiro",
    reports: "Relatórios",
    settings: "Configurações",
    users: "Usuários",
    contracts: "Contratos",
    suppliers: "Fornecedores",
    customers: "Clientes",
    activities: "Atividades",
    sites: "Sites",
    categories: "Categorias",
    dashboard: "Painel",
    profile: "Perfil",
    notifications: "Notificações",
    permissions: "Permissões",
    roles: "Papéis",
    audit: "Auditoria",
    logs: "Registros",
    entries: "Lançamentos",
    attributions: "Atribuições",
  };

  const routeMap: Record<string, string> = {
    "/home": "Início",
    "/about": "Sobre",
    "/towermanagement": "Gerenciamento de Torre",
    "/towermanagement/measurements": "Gerenciamento de Torre - Medições",
    "/financial": "Financeiro",
    "/financial/invoices": "Financeiro - Notas Fiscais",
    "/global": "Global",
    "/reports": "Relatórios",
    "/settings": "Configurações",
  };

  if (routeMap[path]) return routeMap[path];

  for (const [route, title] of Object.entries(routeMap)) {
    if (path.startsWith(route) && path !== route) {
      const remaining = path.substring(route.length).split("/").filter(Boolean);
      if (remaining.length > 0) {
        const lastSegment = remaining[remaining.length - 1];
        if (isNaN(Number(lastSegment)) && lastSegment !== "new") {
          const translated =
            translations[lastSegment.toLowerCase()] ||
            lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
          return `${title} - ${translated}`;
        }
      }
      return title;
    }
  }

  const segments = path.split("/").filter(Boolean);
  if (segments.length > 0) {
    const translated = segments
      .filter((seg) => seg !== "new" && isNaN(Number(seg)))
      .map((seg) => {
        const lower = seg.toLowerCase();
        return (
          translations[lower] ||
          seg
            .split(/[-_]/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        );
      });

    if (translated.length > 0) {
      return translated.join(" - ");
    }
  }

  return "Página";
};

export default function RecentPages({
  renderTrigger,
}: {
  renderTrigger?: (
    onClick: (event: React.MouseEvent<HTMLElement>) => void,
    ref: unknown,
    badgeContent: number,
  ) => React.ReactNode;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [recentPages, setRecentPages] = React.useState<PageInfo[]>([]);
  const [favorites, setFavorites] = React.useState<PageInfo[]>([]);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const loadPreferences = React.useCallback(async () => {
    try {
      const { userPreference } = await showUserPreference({
        key: PREFERENCE_KEY,
      });
      if (userPreference?.value) {
        setRecentPages(userPreference.value.recent || []);
        setFavorites(userPreference.value.favorites || []);
      }
      setIsLoaded(true);
    } catch (error: any) {
      console.log("Erro ao carregar preferências ou nenhuma encontrada");
      if (error?.response?.status === 404 || error?.response?.status === 400) {
        setIsLoaded(true);
      }
    }
  }, []);

  const savePreferences = React.useCallback(
    async (recent: PageInfo[], favs: PageInfo[]) => {
      try {
        await createUserPreference({
          userPreference: {
            key: PREFERENCE_KEY,
            value: {
              recent,
              favorites: favs,
            },
          },
        });
      } catch (error) {
        console.error("Erro ao salvar preferências:", error);
      }
    },
    [],
  );

  React.useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  React.useEffect(() => {
    const currentPath = location.pathname;

    const mainRoutes = [
      "/",
      "/home",
      "/about",
      "/towermanagement",
      "/financial",
      "/global",
      "/reports",
      "/settings",
    ];

    if (
      mainRoutes.includes(currentPath) ||
      currentPath.includes("/login") ||
      currentPath.includes("/logout")
    ) {
      return;
    }

    const segments = currentPath.split("/").filter(Boolean);
    if (segments.length < 2) {
      return;
    }

    const pageInfo: PageInfo = {
      path: currentPath,
      title: getPageTitle(currentPath),
      timestamp: new Date().toISOString(),
      isFavorite: favorites.some((f) => f.path === currentPath),
    };

    setRecentPages((prev) => {
      const filtered = prev.filter((p) => p.path !== currentPath);
      const updated = [pageInfo, ...filtered].slice(0, MAX_RECENT_PAGES);
      if (isLoaded) {
        savePreferences(updated, favorites);
      }
      return updated;
    });
  }, [location.pathname, favorites, savePreferences, isLoaded]);

  const toggleFavorite = (page: PageInfo, event: React.MouseEvent) => {
    event.stopPropagation();

    setFavorites((prev) => {
      const isFavorite = prev.some((f) => f.path === page.path);
      let updated: PageInfo[];

      if (isFavorite) {
        updated = prev.filter((f) => f.path !== page.path);
      } else {
        if (prev.length >= MAX_FAVORITES) {
          updated = [...prev.slice(1), { ...page, isFavorite: true }];
        } else {
          updated = [...prev, { ...page, isFavorite: true }];
        }
      }

      setRecentPages((recentPrev) => {
        const updatedRecent = recentPrev.map((p) =>
          p.path === page.path ? { ...p, isFavorite: !isFavorite } : p,
        );
        savePreferences(updatedRecent, updated);
        return updatedRecent;
      });

      return updated;
    });
  };

  const navigateToPage = (path: string) => {
    navigate(path);
    handleClose();
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return "Agora";
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  return (
    <Box alignSelf="center" sx={{ width: renderTrigger ? "100%" : "auto" }}>
      <React.Fragment>
        {renderTrigger ? (
          renderTrigger(handleClick, null, favorites.length)
        ) : (
          <Box
            sx={{ display: "flex", alignItems: "center", textAlign: "center" }}
          >
            <Tooltip title="Páginas Recentes e Favoritas">
              <IconButton
                onClick={handleClick}
                size="large"
                aria-controls={open ? "recent-pages-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <Badge
                  badgeContent={favorites.length}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      minWidth: 16,
                      height: 16,
                      backgroundColor: "#ffa726",
                      color: "#fff",
                    },
                  }}
                >
                  <HistoryIcon
                    sx={{
                      fontSize: 26,
                      color: "#575B64",
                      transition: "all 0.2s",
                    }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Menu
          anchorEl={anchorEl}
          id="recent-pages-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 4px 20px rgba(0,0,0,0.12))",
              mt: 1.5,
              ml: 1.5,
              borderRadius: 2,
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: "50%",
                left: -5,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Histórico de Navegação
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Acesso rápido às suas páginas
            </Typography>
          </Box>

          <List
            sx={{
              width: "100%",
              minWidth: 400,
              maxWidth: 400,
              maxHeight: 500,
              overflow: "auto",
              bgcolor: "background.paper",
              py: 0,
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(0,0,0,0.05)",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.2)",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.3)",
                },
              },
            }}
          >
            {/* Favoritos */}
            {favorites.length > 0 && (
              <>
                <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="warning.main"
                  >
                    ★ FAVORITAS ({favorites.length})
                  </Typography>
                </Box>
                {favorites.map((page, index) => (
                  <React.Fragment key={`fav-${page.path}`}>
                    {index > 0 && (
                      <Divider variant="inset" component="li" sx={{ ml: 0 }} />
                    )}
                    <ListItemButton
                      onClick={() => navigateToPage(page.path)}
                      sx={{
                        py: 1.5,
                        px: 2.5,
                        "&:hover": {
                          backgroundColor: "rgba(255, 167, 38, 0.08)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {page.title}
                            </Typography>
                            <Chip
                              label="Favorito"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                backgroundColor: "rgba(255, 167, 38, 0.1)",
                                color: "warning.main",
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {page.path}
                          </Typography>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => toggleFavorite(page, e)}
                        sx={{ ml: 1 }}
                      >
                        <StarIcon
                          sx={{ fontSize: 20, color: "warning.main" }}
                        />
                      </IconButton>
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </>
            )}

            {/* Páginas Recentes */}
            {recentPages.length > 0 && (
              <>
                <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ color: theme.palette.primary.contrastText }}
                  >
                    RECENTES ({recentPages.length})
                  </Typography>
                </Box>
                {recentPages.map((page, index) => (
                  <React.Fragment key={`recent-${page.path}`}>
                    {index > 0 && (
                      <Divider variant="inset" component="li" sx={{ ml: 0 }} />
                    )}
                    <ListItemButton
                      onClick={() => navigateToPage(page.path)}
                      sx={{
                        py: 1.5,
                        px: 2.5,
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2" fontWeight={500}>
                              {page.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", fontStyle: "italic" }}
                            >
                              {formatTime(page.timestamp)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {page.path}
                          </Typography>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => toggleFavorite(page, e)}
                        sx={{ ml: 1 }}
                      >
                        {page.isFavorite ? (
                          <StarIcon
                            sx={{ fontSize: 20, color: "warning.main" }}
                          />
                        ) : (
                          <StarBorderIcon
                            sx={{
                              fontSize: 20,
                              color: "text.secondary",
                              "&:hover": { color: "warning.main" },
                            }}
                          />
                        )}
                      </IconButton>
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </>
            )}

            {/* Estado vazio */}
            {recentPages.length === 0 && favorites.length === 0 && (
              <Box
                sx={{
                  p: 6,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <HistoryIcon
                  sx={{
                    fontSize: 48,
                    color: "text.disabled",
                    mb: 1,
                  }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="text.primary"
                >
                  Nenhuma página visitada
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Suas páginas recentes aparecerão aqui
                </Typography>
              </Box>
            )}
          </List>
        </Menu>
      </React.Fragment>
    </Box>
  );
}
